import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Users, 
  UserPlus, 
  Briefcase, 
  FileText, 
  CheckSquare, 
  Calendar, 
  Clock, 
  Receipt, 
  Wallet,
  MessageSquare,
  Sparkles,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Scale,
  UsersRound,
  Brain,
  Mail,
  DollarSign,
  Bell,
  History,
  FormInput,
  FolderOpen,
  FileOutput,
  Shield,
  Search,
  PenTool,
  Building2
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { NotificationBell } from '../common/NotificationBell';
import { AIChatWidget } from '../common/AIChatWidget';
import { GlobalSearch } from '../common/GlobalSearch';
import { FirmSwitcher } from '../common/FirmSwitcher';
import { TrialBanner } from '../common/TrialBanner';
import { TrialExpiredModal } from '../common/TrialExpiredModal';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';

// Streamlined navigation - organized by user workflow priority
const navSections = [
  {
    title: null,
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    ]
  },
  {
    title: 'Work',
    items: [
      { path: '/cases', label: 'Cases', icon: Briefcase },
      { path: '/clients', label: 'Clients', icon: Users },
      { path: '/tasks', label: 'Tasks', icon: CheckSquare },
      { path: '/calendar', label: 'Calendar', icon: Calendar },
      { path: '/documents', label: 'Documents', icon: FileText },
    ]
  },
  {
    title: 'Finance',
    items: [
      { path: '/time-billing', label: 'Time & Billing', icon: Clock },
      { path: '/trust', label: 'Trust Account', icon: Wallet },
      { path: '/reports', label: 'Reports', icon: BarChart3 },
    ]
  },
  {
    title: 'Communication',
    items: [
      { path: '/email', label: 'Email', icon: Mail },
      { path: '/team-chat', label: 'Team Chat', icon: MessageSquare },
    ]
  },
  {
    title: 'Tools',
    items: [
      { path: '/forms', label: 'Forms', icon: FormInput },
      { path: '/ai-assistant', label: 'AI Assistant', icon: Sparkles },
    ]
  },
];

// Admin/Settings items - collapsible section
const adminItems = [
  { path: '/team', label: 'Team', icon: UsersRound },
  { path: '/firm-settings', label: 'Firm Settings', icon: Building2 },
  { path: '/settings', label: 'Preferences', icon: Settings },
];

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);

  // Global keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setGlobalSearchOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Get current page title
  const getCurrentPageTitle = () => {
    const allItems = [...navSections.flatMap(s => s.items), ...adminItems];
    return allItems.find(item => item.path === location.pathname)?.label || 'Dashboard';
  };

  // Check if current path is in admin section
  const isAdminPath = adminItems.some(item => item.path === location.pathname);

  return (
    <div className="h-screen flex overflow-hidden bg-slate-50" data-testid="dashboard-layout">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed lg:static inset-y-0 left-0 z-50 w-64 flex-shrink-0 bg-slate-900 flex flex-col transform transition-transform duration-200 ease-in-out",
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-5 border-b border-slate-800">
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <div className="p-1.5 bg-blue-600 rounded-lg">
              <Scale className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg font-bold text-white tracking-tight">LEAP Pro</span>
          </Link>
          <button 
            className="lg:hidden text-slate-400 hover:text-white transition-colors"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Quick Search */}
        <div className="px-4 py-3 border-b border-slate-800">
          <button
            onClick={() => setGlobalSearchOpen(true)}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-400 bg-slate-800 border border-slate-700 rounded-lg hover:text-slate-300 hover:border-slate-600 transition-colors"
            data-testid="quick-search-btn"
          >
            <Search className="h-4 w-4" />
            <span className="flex-1 text-left">Search...</span>
            <kbd className="hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border border-slate-600 bg-slate-700 px-1.5 font-mono text-[10px] font-medium text-slate-400">
              ⌘K
            </kbd>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-3 px-3 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
          {navSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className={sectionIndex > 0 ? 'mt-5' : ''}>
              {section.title && (
                <h3 className="px-3 mb-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  {section.title}
                </h3>
              )}
              <ul className="space-y-0.5">
                {section.items.map((item) => {
                  const isActive = location.pathname === item.path || 
                                   (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
                  const Icon = item.icon;

                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150",
                          isActive 
                            ? "bg-blue-600 text-white shadow-lg shadow-blue-600/25" 
                            : "text-slate-400 hover:text-white hover:bg-slate-800"
                        )}
                        data-testid={`nav-${item.path.replace('/', '')}`}
                      >
                        <Icon className="h-[18px] w-[18px] flex-shrink-0" />
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {/* Admin Section - Collapsible */}
          <div className="mt-5">
            <Collapsible open={adminOpen || isAdminPath} onOpenChange={setAdminOpen}>
              <CollapsibleTrigger className="flex items-center justify-between w-full px-3 mb-2 group">
                <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  Administration
                </h3>
                <ChevronRight className={cn(
                  "h-4 w-4 text-slate-500 transition-transform duration-200",
                  (adminOpen || isAdminPath) && "rotate-90"
                )} />
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-0.5">
                {adminItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  const Icon = item.icon;

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150",
                        isActive 
                          ? "bg-blue-600 text-white shadow-lg shadow-blue-600/25" 
                          : "text-slate-400 hover:text-white hover:bg-slate-800"
                      )}
                      data-testid={`nav-${item.path.replace('/', '')}`}
                    >
                      <Icon className="h-[18px] w-[18px] flex-shrink-0" />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </CollapsibleContent>
            </Collapsible>
          </div>
        </nav>

        {/* User Profile Section */}
        <div className="p-3 border-t border-slate-800">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-slate-800 transition-colors text-left">
                <Avatar className="h-9 w-9 ring-2 ring-slate-700">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-blue-600 text-white text-sm font-medium">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                  <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                </div>
                <ChevronDown className="h-4 w-4 text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.role} • {user?.subscription_tier || 'Basic'} Plan</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <Shield className="h-4 w-4 mr-2" />
                Security
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut className="h-4 w-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6" data-testid="header">
          <div className="flex items-center gap-3">
            <button 
              className="lg:hidden p-2 -ml-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-base font-semibold text-slate-900">
                {getCurrentPageTitle()}
              </h1>
            </div>
            
            {/* Firm Switcher - only visible when user has multiple firms */}
            <div className="hidden md:block border-l border-slate-200 pl-3 ml-1">
              <FirmSwitcher />
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Quick Actions */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/cases')}
              className="hidden sm:flex gap-2 h-8"
            >
              <Briefcase className="h-3.5 w-3.5" />
              New Case
            </Button>

            {/* Notification Bell */}
            <NotificationBell />

            {/* Mobile User Menu */}
            <div className="lg:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <Avatar className="h-7 w-7">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback className="bg-blue-600 text-white text-xs">
                        {getInitials(user?.name)}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/settings')}>
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="h-4 w-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Trial Expiry Banner */}
        <TrialBanner />

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6" data-testid="main-content">
          {children}
        </main>
      </div>

      {/* AI Chat Widget - Floating button */}
      <AIChatWidget />
      
      {/* Global Search Dialog */}
      <GlobalSearch 
        open={globalSearchOpen} 
        onOpenChange={setGlobalSearchOpen}
      />
      
      {/* Trial Expired Modal - Blocks access when trial is fully expired */}
      <TrialExpiredModal />
    </div>
  );
};

export default DashboardLayout;
